import { useIntl } from '@/intl'
import { useShowScrollButton } from '@/scopes/common/hooks/useShowScrollButton'
import { usePDPSelector, usePDPStore } from '@/scopes/products/infrastructure/usePDPStore'
import { Button, IconButton } from '@sunwebgroup/ui-components/uds'
import cn from 'classnames'
import useEmblaCarousel from 'embla-carousel-react'
import styles from './AnchorMenu.module.css'
import { useAnchorMenu } from './hooks'
import { AnchorHref } from './types'

function getMenuItems(
  showRoomTypes: boolean,
  showFacilities: boolean,
  showDestination: boolean,
  showSkiAncillaries: boolean,
) {
  return [
    { label: 'accomodation_anchor_menu_general', anchorHref: AnchorHref.General },
    showRoomTypes && { label: 'accomodation_anchor_menu_roomtypes', anchorHref: AnchorHref.Roomtypes },
    showFacilities && { label: 'accomodation_anchor_menu_facilities', anchorHref: AnchorHref.Facilities },
    { label: 'accomodation_anchor_menu_practical_info', anchorHref: AnchorHref.PracticalInfo },
    { label: 'accomodation_anchor_menu_reviews', anchorHref: AnchorHref.Reviews },
    showDestination && { label: 'accommodation_anchor_menu_destination', anchorHref: AnchorHref.Destination },
    showSkiAncillaries && { label: 'accomodation_anchor_menu_ski', anchorHref: AnchorHref.SkiAncillaries },
  ].filter(Boolean) as { label: string, anchorHref: AnchorHref }[]
}

interface AnchorMenuProps {
  showRoomTypesMenu?: boolean
  showFacilitiesMenu?: boolean
  showDestinationMenu?: boolean
  showSkiAncillariesMenu?: boolean
}

export default function AnchorMenu({
  showRoomTypesMenu: showRoomTypes = true,
  showFacilitiesMenu: showFacilities = true,
  showDestinationMenu: showDestination = true,
  showSkiAncillariesMenu: showSkiAncillaries = false,
}: AnchorMenuProps) {
  const intl = useIntl()
  const store = usePDPStore()
  const activeAnchorHref = usePDPSelector(state => state.activeAnchorHref)
  const setActiveAnchorHref = store.setActiveAnchorHref
  // TODO: Refactor to use useAnalytics hook for everything in the click event
  const { handleAnchorClick: sendClickEvent } = useAnchorMenu()

  const visibleMenuItems = getMenuItems(showRoomTypes, showFacilities, showDestination, showSkiAncillaries)

  const handleAnchorClick = (anchor: AnchorHref) => {
    document.getElementById(anchor)?.scrollIntoView({
      behavior: 'smooth',
    })
    setActiveAnchorHref(anchor)
    sendClickEvent(anchor)
  }

  const [emblaRef, emblaApi] = useEmblaCarousel({
    dragFree: true,
  })

  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } = useShowScrollButton(emblaApi)

  return (
    <div className={styles.tapBar}>
      {!prevBtnDisabled && (
        <div className={styles.buttonWrapperLeft}>
          <IconButton
            className={styles.navButton}
            glyph="ChevronLeftSmall"
            onClick={onPrevButtonClick}
            disabled={prevBtnDisabled}
            size="XS"
            variant="secondary"
            aria-label={intl.formatMessage({ id: 'accomodation_anchor_menu_prev_button' })}
          />
        </div>
      )}
      <nav className={styles.slider} ref={emblaRef}>
        <div className={styles.menuContainer} role="tablist" aria-label={intl.formatMessage({ id: 'accomodation_anchor_menu_navigation' })}>
          {visibleMenuItems.map((item) => {
            const isActive = activeAnchorHref === item.anchorHref
            return (
              <Button
                className={cn(styles.menuButton, { [styles.menuButtonActive]: isActive })}
                onClick={() => handleAnchorClick(item.anchorHref)}
                variant={isActive ? 'transparentPrimary' : 'transparentNeutral'}
                size="M"
                key={item.anchorHref}
                role="tab"
                aria-selected={isActive}
                aria-controls={item.anchorHref}
                id={`tab-${item.anchorHref}`}
              >
                {intl.formatMessage({ id: item.label })}
              </Button>
            )
          })}
        </div>
      </nav>
      {!nextBtnDisabled && (
        <div className={styles.buttonWrapperRight}>
          <IconButton
            className={styles.navButton}
            glyph="ChevronRightSmall"
            onClick={onNextButtonClick}
            disabled={nextBtnDisabled}
            size="XS"
            variant="secondary"
            aria-label={intl.formatMessage({ id: 'accomodation_anchor_menu_next_button' })}
          />
        </div>
      )}
    </div>
  )
}
